import React, {useCallback, useEffect, useState} from "react";
import {
    Box,
    Button,
    Combobox,
    InputBase,
    useCombobox
} from "@mantine/core";
import {
    IconPlayerPlay,
    IconPlayerPause,
    IconPlus
} from "@tabler/icons-react";
import dayjs, {Dayjs} from "dayjs";
import {v4 as uuidv4} from "uuid";

import {EventType, EventFormat, getEventTypeLabel, TEventId} from "@/common/types";
import {ICreateEventDTO} from "../types/dtos/ICreateEventDTO";
import {useNeurotuneStore} from "../store/neurotune";
import {TSubjectId} from "../types";
import {TProtocolId, TProtocolStepId} from "@/protocols/types/protocol.ts";
import {EventDetailInput} from "./EventDetailInput";
import {PPReliefEventDetail} from "./PPReliefEventDetail";
import classes from "./EventRecorder.module.css";
import {createEvent as createSubjectEvent} from "@/common/services/eventsService.ts";
import {IProtocolStepEvent, TProtocolStepEventId} from "@/protocols/types/IProtocolStepEvent.ts";
import {ZoneAwareDateTimePicker} from "@/common/components/ZoneAwareDateTimePicker.tsx";

// Event types shown in the recorder dropdown (add new recordable types here)
const RECORDABLE_EVENT_TYPES: Array<EventType> = [
    EventType.NOTE,
    EventType.PP_RELIEF,
];

interface EventRecorderProps {
    subjectId: TSubjectId
    protocolId?: TProtocolId,
    stepId?: TProtocolStepId
    timezone: string
    autoStart?: boolean
    /**
     * Single creation hook. Required when `protocolId` is set; the
     * caller decides how to persist (pending-writes, direct API call,
     * anything else) and is responsible for updating local state with
     * the returned event. Returns the optimistic (pending-writes) or
     * server (direct) event.
     *
     * The recorder assigns the id's for the EventId and the ProtocolStepEventId
     * join table so the ids flow through the * optimistic UI, the WAL
     * coalesceKey, and the eventual server create_event with no need for a
     * tempId → realId translation. Callers should use
     * it as the delete target and the optimistic-state key.
     */
    createEvent?: (
        eventData: IProtocolStepEvent,
    ) => Promise<IProtocolStepEvent>
}

export const EventRecorder: React.FC<EventRecorderProps> = ({
                                                                subjectId,
                                                                protocolId,
                                                                stepId,
                                                                timezone,
                                                                createEvent,
                                                                autoStart = true
                                                            }) => {
    const activeCompany = useNeurotuneStore((state) => state.activeCompany);
    const [isPlaying, setIsPlaying] = useState(autoStart);
    const [currentTime, setCurrentTime] = useState(dayjs().tz(timezone));
    const [eventType, setEventType] = useState<EventType>(EventType.NOTE);
    const [{format, details}, setDetails] = useState<{
        format: EventFormat,
        details: string
    }>({format: EventFormat.TEXT, details: ""});
    // Score state for PP_RELIEF events
    const [score, setScore] = useState<number | undefined>(undefined);

    const currentTimeInZone = () => dayjs().tz(timezone);

    useEffect(() => {
        let intervalId: number | undefined;
        if (isPlaying) {
            intervalId = window.setInterval(() => {
                setCurrentTime(currentTimeInZone())
            }, 1000);
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isPlaying]);

    const handlePlayPause = () => {
        setIsPlaying(!isPlaying);
        if (!isPlaying) {
            setCurrentTime(currentTimeInZone());
        }
    };

    const onDetailsChange = (format: EventFormat, details: string) => {
        setDetails({format, details});
    }

    const handleTimeChange = (date: Dayjs | null) => {
        if (date) {
            setIsPlaying(false);
            setCurrentTime(date);
        }
    };

    const buildMetadata = (): Record<string, unknown> => {
        if (eventType === EventType.PP_RELIEF) {
            return {score};
        }
        return {format, detail: details};
    };

    const createEventWithStep = useCallback(async () => {
        const metadata = buildMetadata();
        const dto: ICreateEventDTO = {
            subject_id: subjectId,
            event_type: eventType,
            metadata,
            tags: [],
            start_time: currentTime.toISOString(),
        };

        try {
            if (protocolId) {
                if (!createEvent) {
                    throw new Error(
                        'EventRecorder: createEvent callback is required when protocolId is set',
                    )
                }
                const pstep_event_id = uuidv4() as TProtocolStepEventId
                const event_id = uuidv4() as TEventId
                const eventData: IProtocolStepEvent = {
                    id: pstep_event_id,
                    protocolId: protocolId,
                    protocolStepId: (stepId ?? null) as TProtocolStepId,
                    event: {
                        id: event_id ,
                        subjectId,
                        deviceId: undefined,
                        eventType,
                        metadata,
                        tags: [],
                        startTime: dto.start_time,
                        endTime: undefined,
                        createdAt: dayjs().toISOString(),
                    },
                }
                await createEvent(eventData)
            } else {
                // Non-protocol subject event (only exercised in tests;
                // production callers always provide protocolId).
                await createSubjectEvent(activeCompany.id, dto);
            }
            // Reset per-type detail state
            setDetails({format: EventFormat.TEXT, details: ""});
            setScore(undefined);
            setCurrentTime(currentTimeInZone());
            if (!isPlaying) {
                setIsPlaying(true);
            }
        } catch (error) {
            console.error("Failed to create event:", error);
        }
    }, [subjectId, eventType, details, format, score, currentTime, timezone, stepId, activeCompany, protocolId, createEvent]);

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    return (
        <Box className={classes.container}>

            <Box
                className={classes.playPauseButton}
                onClick={handlePlayPause}
                title={isPlaying ? "Pause" : "Play"}
            >
                {isPlaying ? <IconPlayerPause size="1em"/> : <IconPlayerPlay size="1em"/>}
            </Box>

            <ZoneAwareDateTimePicker
                className={classes.timePicker}
                dateTimeProps={{
                    size: "xs", placeholder: "Select date and time",
                    timeInputProps: {"aria-label": "time-input"},
                    onClick: () => {
                        if (isPlaying) {
                            setIsPlaying(false);
                        }
                    }
                }}
                value={currentTime}
                timezone={timezone}
                onChange={handleTimeChange}
                label="Event Time"
            />

            <Combobox
                store={combobox}
                onOptionSubmit={(val) => {
                    setEventType(val as EventType);
                    combobox.closeDropdown();
                }}
            >
                <Combobox.Target>
                    <InputBase
                        className={classes.eventType}
                        size="xs"
                        label="Event Type"
                        value={getEventTypeLabel(eventType)}
                        readOnly
                        onClick={() => {
                            if (isPlaying) {
                                setIsPlaying(false);
                            }
                            combobox.toggleDropdown()
                        }}
                        rightSection={<Combobox.Chevron/>}
                        rightSectionPointerEvents="none"
                    />
                </Combobox.Target>
                <Combobox.Dropdown className={classes.eventTypeDropdown}>
                    <Combobox.Options>
                        {RECORDABLE_EVENT_TYPES.map((type) => (
                            <Combobox.Option key={type} value={type} className={classes.eventTypeOption}>
                                {getEventTypeLabel(type)}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>

            {eventType === EventType.PP_RELIEF ? (
                <PPReliefEventDetail
                    score={score}
                    onChange={setScore}
                    readonly={false}
                    showLabel={true}
                    onPauseRequest={() => setIsPlaying(false)}
                />
            ) : (
                <EventDetailInput
                    format={format}
                    details={details}
                    isPlaying={isPlaying}
                    onDetailChange={onDetailsChange}
                    onSubmit={() => createEventWithStep()}
                    onPauseRequest={() => setIsPlaying(false)}
                />
            )}

            <Box className={classes.createButtonContainer}>
                <Button
                    size="xs"
                    onClick={() => createEventWithStep()}
                    leftSection={<IconPlus color={"var(--bios-green-blue-9)"} size="1.5em"/>}
                >
                    Create
                </Button>
            </Box>
        </Box>
    );
};
